import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import '../styles/graphite-green.css';
import '../styles/platform-flutter.css';
import '../styles/interaction-accent.css';
import '../styles/semantic-text.css';
import { BrandThemeFilters } from '../components/BrandThemeFilters';

export type ThemePreference = 'light' | 'graphite-green';
type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'vo2_theme_preference';

export function readThemePreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'graphite-green') return stored;
    if (stored === 'dark' || stored === 'system') {
      localStorage.setItem(STORAGE_KEY, 'graphite-green');
      return 'graphite-green';
    }
    localStorage.setItem(STORAGE_KEY, 'light');
  } catch {
    // Use the light presentation when storage is unavailable.
  }
  return 'light';
}

function resolveTheme(preference: ThemePreference): ResolvedTheme {
  return preference === 'light' ? 'light' : 'dark';
}

export function applyThemePreference(preference: ThemePreference): ResolvedTheme {
  const resolved = resolveTheme(preference);
  document.documentElement.dataset.designSystem = 'flutter';
  document.documentElement.dataset.theme = resolved;
  document.documentElement.dataset.themePreference = preference;
  document.documentElement.dataset.appearance = preference === 'graphite-green' ? 'graphite-green' : 'default';
  document.documentElement.style.colorScheme = resolved;
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content',
    getComputedStyle(document.documentElement).getPropertyValue('--palette-page').trim());
  return resolved;
}

export function initializeTheme(): void {
  applyThemePreference(readThemePreference());
}

interface ThemeContextValue {
  preference: ThemePreference;
  resolvedTheme: ResolvedTheme;
  setPreference: (preference: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(readThemePreference);
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => resolveTheme(preference));

  useEffect(() => {
    setResolvedTheme(applyThemePreference(preference));
    try {
      localStorage.setItem(STORAGE_KEY, preference);
    } catch {
      // Keep the in-memory selection when storage is unavailable.
    }

  }, [preference]);

  const value = useMemo<ThemeContextValue>(() => ({
    preference,
    resolvedTheme,
    setPreference: setPreferenceState,
  }), [preference, resolvedTheme]);

  return <ThemeContext.Provider value={value}><BrandThemeFilters />{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);
  if (!value) throw new Error('useTheme must be used within ThemeProvider');
  return value;
}
